import { useState, useEffect, useRef } from 'react';
import { X, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { FamSpinner } from '../badges/Badge';
import { createSigningSession } from '../../lib/docuseal';

export type ContractType = 'creator' | 'investor' | 'dealer';

export interface ContractPanelProps {
  contractType: ContractType;
  signerEmail: string;
  signerName: string;
  fields?: Array<{ name: string; value: string }>;
  onComplete: () => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Static content per contract type
// ---------------------------------------------------------------------------

const CONTRACT_TITLES: Record<ContractType, string> = {
  creator: 'Diminishing Musharaka Agreement',
  investor: 'Investor Participation Agreement',
  dealer: 'Dealer Purchase Agreement',
};

const DISCLOSURES: Record<ContractType, string[]> = {
  creator: [
    'This agreement establishes a co-ownership structure under Diminishing Musharaka principles.',
    'Your monthly payment consists of Ijarah rent on the unowned portion plus a unit purchase installment.',
    'Upon completing all payments, full title transfers to you with no further obligations.',
    'Late payments may affect your FAM Score and future financing eligibility.',
    'FAM retains ≤10% ownership throughout the agreement term.',
  ],
  investor: [
    'Your investment grants fractional LLC ownership in the financed vehicle.',
    'Returns are earned through the Ijarah (rent) portion of the creator\'s monthly payments.',
    'Capital is returned as the creator purchases ownership units over the term.',
    'Investment is subject to vehicle depreciation and creator repayment performance.',
    'DAO governance rights activate once the campaign is fully funded.',
  ],
  dealer: [
    'FAM purchases your vehicle outright upon successful campaign completion.',
    'Offer is based on estimated book value; final amount confirmed after VIN verification.',
    'Funds are transferred within 5 business days of title transfer and proofing completion.',
    'Standing offer remains valid for 30 days from the date of this agreement.',
  ],
};


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ContractPanel({ contractType, signerEmail, signerName, fields, onComplete, onClose }: ContractPanelProps) {
  const [status, setStatus] = useState<'loading' | 'iframe' | 'fallback' | 'error'>('loading');
  const [embedUrl, setEmbedUrl] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [agreed, setAgreed] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Attempt to get Docuseal embed URL
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const docusealFields = (fields ?? []).map(({ name, value }) => ({
          name,
          default_value: value,
        }));

        const session = await createSigningSession({
          contractType,
          signerEmail,
          signerName,
          fields: docusealFields,
        });

        if (!cancelled) {
          setEmbedUrl(session.embedUrl);
          setStatus('iframe');
        }
      } catch (err: unknown) {
        if (!cancelled) {
          if (err && (err as { code?: string }).code === 'NOT_CONFIGURED') {
            setStatus('fallback');
          } else {
            setErrorMsg(err instanceof Error ? err.message : 'Could not load contract');
            setStatus('error');
          }
        }
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractType]);

  // Listen for Docuseal iframe completion postMessage
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'completed' || e.data?.type === 'docuseal:completed') {
        onComplete();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onComplete]);

  const title = CONTRACT_TITLES[contractType];
  const termRows = (fields ?? []).map(({ name, value }) => ({ label: name, value }));
  const disclosures = DISCLOSURES[contractType];

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      {/* Card */}
      <div className="relative z-10 w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-base">{title}</h2>
              <p className="text-xs text-gray-400">Review and sign to continue</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto min-h-0">

          {/* Loading */}
          {status === 'loading' && (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
              <FamSpinner size={56} label="Preparing your contract..." />
            </div>
          )}

          {/* Docuseal iframe */}
          {status === 'iframe' && (
            <iframe
              ref={iframeRef}
              src={embedUrl}
              className="w-full"
              style={{ height: '560px', border: 'none' }}
              title="Sign Contract"
            />
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 px-6">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <p className="text-sm text-gray-600 text-center">{errorMsg}</p>
              <button onClick={onClose} className="text-sm text-indigo-600 hover:underline">Cancel</button>
            </div>
          )}

          {/* Fallback: styled contract preview + agree checkbox */}
          {status === 'fallback' && (
            <div className="p-6 space-y-6">

              {/* Terms table */}
              {termRows.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold tracking-widest text-gray-400 uppercase mb-3">
                    Contract Terms
                  </h3>
                  <div className="rounded-xl border border-gray-100 overflow-hidden divide-y divide-gray-50">
                    {termRows.map((row, i) => (
                      <div key={i} className="flex justify-between items-center px-4 py-3 bg-white hover:bg-gray-50">
                        <span className="text-sm text-gray-500">{row.label}</span>
                        <span className="text-sm font-semibold text-gray-900">{row.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Disclosures */}
              <div>
                <h3 className="text-xs font-bold tracking-widest text-gray-400 uppercase mb-3">
                  Key Disclosures
                </h3>
                <ul className="space-y-2">
                  {disclosures.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-indigo-400 font-bold mt-0.5 flex-shrink-0">·</span>
                      {d}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Agree checkbox */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer flex-shrink-0"
                />
                <span className="text-sm text-gray-700 leading-snug">
                  I have read and agree to the{' '}
                  <span className="font-semibold">{title}</span>.
                  I understand this is a legally binding document.
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Footer — only for fallback */}
        {status === 'fallback' && (
          <div className="flex gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              onClick={onComplete}
              disabled={!agreed}
              className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-medium hover:bg-indigo-700 disabled:opacity-40 transition-colors text-sm flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Sign & Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
